import { useState } from "react";
import type { FormEvent } from "react";

const FORM_ADDR = "https://readdy.ai/api/form/dagk723ep053ijub2btg";

type Status = "idle" | "loading" | "success" | "error";

export default function Newsletter() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const formDataObj = new URLSearchParams();

    const honeypot = String(data.get("website_alt") || "").trim();
    // 蜜罐被填写则按成功反馈处理，不真正提交
    if (honeypot) {
      form.reset();
      setStatus("success");
      setFormError("");
      return;
    }

    const email = String(data.get("email") || "").trim();
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      setStatus("error");
      setFormError("请填写你的邮箱地址。");
      return;
    }
    if (!emailRe.test(email)) {
      setStatus("error");
      setFormError("邮箱格式似乎不对，请检查后再试。");
      return;
    }

    // 组装待提交字段（跳过空值）
    data.forEach((value, key) => {
      if (key === "website_alt") return;
      const v = String(value).trim();
      if (v) formDataObj.append(key, v);
    });

    setStatus("loading");
    setFormError("");
    try {
      const res = await fetch(FORM_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formDataObj.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try {
        parsed = responseText ? JSON.parse(responseText) : {};
      } catch {
        parsed = {};
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || "";
      if (
        res.ok &&
        parsed?.code === "OK" &&
        !/spam/i.test(responseText) &&
        !/spam/i.test(serverMsg)
      ) {
        form.reset();
        setStatus("success");
        return;
      }
      setStatus("error");
      setFormError(serverMsg || "提交失败，请稍后再试。");
    } catch {
      setStatus("error");
      setFormError("网络出了点问题，请稍后再试。");
    }
  }

  return (
    <section id="newsletter" className="bg-primary-600">
      <div className="mx-auto grid max-w-[1280px] grid-cols-1 items-center gap-8 px-4 py-14 md:px-6 md:py-20 lg:grid-cols-2">
        <div className="text-background-50">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-background-50/15 px-3 py-1 text-xs font-medium">
            <i className="ri-mail-send-line" aria-hidden="true"></i>
            风向周报 · 每周一更新
          </span>
          <h2 className="mt-4 font-heading text-2xl font-bold leading-snug md:text-3xl">
            每周一早上，收到本周最值得吃的塌房舆论复盘
          </h2>
          <p className="mt-3 max-w-md text-[15px] leading-relaxed text-background-50/85">
            订阅后你将定期收到编辑部整理的热点榜单、反应分类与深度洞察。不出站也能看清粉圈风向，随时退订。
          </p>
        </div>

        <div className="w-full">
          <form
            data-readdy-form="星闻雷达风向周报订阅"
            onSubmit={handleSubmit}
            noValidate
            className="rounded-xl bg-background-50 p-6 md:p-7"
          >
            <p className="text-sm font-medium text-foreground-950">订阅风向周报</p>

            {/* honeypot (anti-spam) */}
            <div className="hp-field" aria-hidden="true">
              <label htmlFor="hp-website">网站（请勿填写）</label>
              <input
                type="text"
                id="hp-website"
                name="website_alt"
                autoComplete="off"
                tabIndex={-1}
                readOnly
              />
            </div>

            <div className="mt-4 flex flex-col gap-3 sm:flex-row">
              <label className="flex-1">
                <span className="sr-only">昵称</span>
                <input
                  type="text"
                  name="name"
                  placeholder="怎么称呼你（选填）"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                />
              </label>
            </div>
            <label className="mt-3 block">
              <span className="sr-only">邮箱</span>
              <input
                type="email"
                name="email"
                required
                placeholder="你的邮箱地址"
                className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
              />
            </label>

            {status === "success" && (
              <p className="mt-3 rounded-md bg-secondary-100 px-3 py-2.5 text-sm text-secondary-900">
                订阅成功，感谢你的关注，我们下期见。
              </p>
            )}
            {status === "error" && formError && (
              <p className="mt-3 rounded-md bg-accent-100 px-3 py-2.5 text-sm text-accent-900">
                {formError}
              </p>
            )}

            <button
              type="submit"
              disabled={status === "loading"}
              className="mt-4 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-[15px] font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60"
            >
              {status === "loading" ? (
                <>
                  <i className="ri-loader-4-line animate-spin" aria-hidden="true"></i>
                  提交中…
                </>
              ) : (
                <>
                  立即订阅
                  <i className="ri-arrow-right-line" aria-hidden="true"></i>
                </>
              )}
            </button>
            <p className="mt-3 text-xs leading-relaxed text-foreground-500">
              订阅即表示同意接收本站周报邮件，你可随时取消订阅。
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}